<template>
  <v-container class="pa-8">
    <v-dialog v-model="showOrder" max-width="1400" scrollable>
      <v-card :disabled="loadingOrder" :loading="loadingOrder" rounded="xl" class="">
        <v-card-text v-if="order.currentOrder">
          <!-- Header section -->
          <v-card class="mb-4" flat color="transparent">
            <v-card-title class="textDefault text-h2 font-weight-black"
              >Order {{ order.currentOrder.id }}
              <v-spacer />
              <div>
                <v-img height="60" width="130" src="@/assets/images/sigfloLogoTempDark.png" />
              </div>
            </v-card-title>
            <v-card-text>
              <div>
                Ship Status:
                {{ formatPascalCase(order.currentOrder.shippingStatus) }}
              </div>
              <div>Ship To: {{ shippingAddress }}</div>
              <!-- Add more order information here -->
            </v-card-text>
          </v-card>

          <!-- Data table section -->
          <v-data-table
            dense
            :headers="orderItemHeaders"
            :items="order.currentOrder.orderItems"
            :options="orderItemTableOptions"
            outlined
            class="elevation-0 rounded-xl pt-1"
            hide-default-footer
          >
            <template v-slot:item.description="{ item }">
              {{ item.description }}
              <v-chip x-small color="#32ff98" v-if="item.customerSupplied" class="ml-2">Customer Supplied</v-chip>
            </template>
            <template v-slot:item.unitPrice="{ item }"> ${{ formatPrice(item.unitPrice) }} </template>
            <template v-slot:item.total="{ item }">
              <span class="font-weight-black"> ${{ formatPrice(item.unitPrice * item.quantity) }} </span>
            </template>
          </v-data-table>
          <v-container class="d-flex">
            <v-row>
              <v-col cols="9"></v-col>
              <v-col>
                <v-card rounded="xl" class="elevation-0" min-height="150">
                  <v-card-text>
                    <v-container>
                      <v-row>
                        <v-col class="nopadding sigfloBodyMedium">Subtotal</v-col>
                        <v-col class="nopadding">${{ formatPrice(order.currentOrder.orderSubtotal) }}</v-col>
                      </v-row>
                      <v-row v-if="order.currentOrder.orderDiscount && order.currentOrder.orderDiscount > 0">
                        <v-col class="nopadding sigfloBodyMedium">Discount</v-col>
                        <v-col class="nopadding">${{ formatPrice(order.currentOrder.orderDiscount) }}</v-col>
                      </v-row>
                      <v-row>
                        <v-col class="nopadding sigfloBodyMedium">Taxes</v-col>
                        <v-col class="nopadding">${{ formatPrice(order.currentOrder.orderTax) }}</v-col>
                      </v-row>
                      <v-row>
                        <v-col class="nopadding sigfloBodyMedium">Shipping</v-col>
                        <v-col class="nopadding">${{ formatPrice(order.currentOrder.orderShipping) }}</v-col>
                      </v-row>
                      <v-row>
                        <v-col class="nopadding"><v-divider /></v-col>
                      </v-row>
                      <v-row>
                        <v-col class="nopadding sigfloBodyLarge textEmphasis text-uppercase">Total</v-col>
                        <v-col class="nopadding sigfloBodyLarge textEmphasis"
                          >${{ formatPrice(order.currentOrder.orderTotal) }}</v-col
                        >
                      </v-row>
                    </v-container>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>
    <change-password-dialog :showDialog="showChangePasswordDialog" @close="showChangePasswordDialog = $event" />
    <span class="black--text pt-4 sigfloH1"> My Account </span>
    <v-card width="1000" class="elevation-0 sigfloCard sigfloNeutralBorder150 mt-8 pt-8">
      <v-tabs vertical v-model="tabModel">
        <v-tab class="text-capitalize mr-8">
          <v-icon left> mdi-account </v-icon>
          Account
        </v-tab>
        <v-tab class="text-capitalize mr-8">
          <v-icon left> mdi-list-box </v-icon>
          Orders
        </v-tab>
        <!-- Account -->
        <v-tab-item>
          <v-card class="pa-8 ma-8 elevation-0 sigfloCard">
            <v-card-text>
              <v-form submit-prevent>
                <v-text-field label="Email" clearable v-model="email" filled disabled readonly> </v-text-field>
                <v-text-field label="Password" clearable v-model="password" filled type="password" disabled readonly>
                </v-text-field>
                <v-btn rounded class="sigfloButton400 text-capitalize mb-4" color="primary" @click="changePassword"
                  >Change Password</v-btn
                >
                <v-text-field label="First Name" clearable v-model="firstName" filled disabled readonly> </v-text-field>
                <v-text-field label="Last Name" clearable v-model="lastName" filled disabled readonly> </v-text-field>
              </v-form>
            </v-card-text>
          </v-card>
        </v-tab-item>
        <!-- Orders -->
        <v-tab-item>
          <order-table @loadOrder="loadOrder" @refreshOrders="refreshOrders" />
        </v-tab-item>
      </v-tabs>
    </v-card>
  </v-container>
</template>

<script>
  import { mapState } from 'vuex';
  import orderTable from '@/components/orders/OrderTable.vue';
  import changePasswordDialog from '@/components/authentication/ChangePasswordDialog.vue';

  export default {
    components: {
      orderTable,
      changePasswordDialog
    },
    props: {
      view: {
        type: String,
        default: 'account'
      },
      orderId: {
        type: Number,
        default: 0
      }
    },
    data() {
      return {
        tabModel: 1,
        showOrder: false,
        email: '',
        password: 'nottherealone',
        firstName: '',
        lastName: '',
        showChangePasswordDialog: false,
        loadingOrder: true,
        orderItemHeaders: [
          { text: 'SKU', value: 'sku' },
          { text: 'Subsystem Name', value: 'subsystemName' },
          { text: 'Description', value: 'description' },
          { text: 'Quantity', value: 'quantity' },
          { text: 'Unit Price', value: 'unitPrice' },
          { text: 'Total', value: 'total' }
        ],
        orderItemTableOptions: {
          itemsPerPage: -1 // Show all rows
        }
      };
    },
    mounted() {
      this.email = this.app.user.email;
      this.firstName = this.app.user.firstName;
      this.lastName = this.app.user.lastName;
    },
    watch: {
      view: function (newVal) {
        if (newVal === 'orders') {
          this.tabModel = 1;
        }
      }
    },
    created() {
      // Check if route has order ID parameter
      const orderId = this.$route.query.orderId;
      if (orderId) {
        // Simulate loading order data (you would typically fetch this from an API)
        this.loadOrder(orderId);
        // remove the orderId from the URL
        const updatedQuery = { ...this.$route.query };
        delete updatedQuery.orderId;
        this.$router.replace({ path: this.$route.path, query: updatedQuery });
      }
    },
    methods: {
      changePassword() {
        this.showChangePasswordDialog = true;
      },
      /**
       * Loads an order by its ID.
       *
       * @param {number} id - The ID of the order to load.
       */
      loadOrder(id) {
        this.loadingOrder = true;
        this.showOrder = true;
        this.$store.dispatch('order/getOrder', id).then(() => {
          this.loadingOrder = false;
        });
      },
      /**
       * Refreshes the orders list
       */
      refreshOrders() {
        this.loadingOrders = false;
        this.$store.dispatch('order/getOrderList').then(() => {
          this.loadingOrders = false;
        });
      }
    },
    computed: {
      ...mapState(['app', 'order']),
      orders() {
        return this.order.orderList;
      },
      shippingAddress() {
        let address = this.order.currentOrder.shippingAddress.Line1;
        if (this.order.currentOrder.shippingAddress.Line2) {
          address += ', ' + this.order.currentOrder.shippingAddress.Line2;
        }
        address += ', ' + this.order.currentOrder.shippingAddress.city;
        address += ', ' + this.order.currentOrder.shippingAddress.state;
        address += ' ' + this.order.currentOrder.shippingAddress.postal_code;

        return address;
      }
    }
  };
</script>

<style scoped>
  .nopadding {
    padding: 2px !important;
  }
</style>
